import {
  DatabaseT,
  WorkGroupT,
  WorkspaceT,
  isStatusActive,
  isStatusDegraded,
  isStatusError,
  isStatusIdle,
  isStatusPaused,
  isStatusPending,
} from '@/pages/workgroup/type';

import { AccessFilter, FilterState, SortKey, StatusFilter, WorkspaceRow } from './types';

export function buildWorkspaceRows(group: WorkGroupT): WorkspaceRow[] {
  return group.workspaces.map((workspace) => ({
    workspace,
    databaseName:
      group.tg_databases.find((db) => db.database_id === workspace.database_id)?.name || workspace.database_name || '',
  }));
}

/** Databases with no associated workspace (e.g. after workspace termination). */
export function getOrphanDatabases(group: WorkGroupT): DatabaseT[] {
  return group.tg_databases.filter(
    (db) => !group.workspaces.some((workspace) => workspace.database_id === db.database_id)
  );
}

/**
 * Order rows so each R/O (snapshot) workspace appears immediately after its
 * related R/W workspace (same database_id). Unrelated R/O rows follow at the end.
 */
export function orderWorkspaceRowsForCardView(rows: WorkspaceRow[]): WorkspaceRow[] {
  const rwRows = rows.filter((row) => row.workspace.is_rw);
  const roRows = rows.filter((row) => !row.workspace.is_rw);
  const ordered: WorkspaceRow[] = [];
  const placedRoIds = new Set<string>();

  for (const rwRow of rwRows) {
    ordered.push(rwRow);
    const databaseId = rwRow.workspace.database_id;
    if (!databaseId) {
      continue;
    }
    for (const roRow of roRows) {
      if (roRow.workspace.database_id === databaseId) {
        ordered.push(roRow);
        placedRoIds.add(roRow.workspace.workspace_id);
      }
    }
  }

  for (const roRow of roRows) {
    if (!placedRoIds.has(roRow.workspace.workspace_id)) {
      ordered.push(roRow);
    }
  }

  return ordered;
}

/**
 * Orphan databases have no workspace attributes — hide them when status/access/size
 * filters are active; otherwise match by search on database name.
 */
export function filterOrphanDatabases(databases: DatabaseT[], filters: FilterState): DatabaseT[] {
  if (filters.status !== 'all' || filters.access !== 'all' || filters.size !== 'all') {
    return [];
  }

  const search = filters.search.trim().toLowerCase();
  const filtered = search ? databases.filter((db) => db.name.toLowerCase().includes(search)) : [...databases];

  return filtered.sort((a, b) => {
    switch (filters.sort) {
      case 'name_desc':
        return b.name.localeCompare(a.name);
      case 'name_asc':
      default:
        return a.name.localeCompare(b.name);
    }
  });
}

export function getUniqueWorkspaceSizes(workspaces: WorkspaceT[]): string[] {
  const sizes = new Set<string>();
  for (const workspace of workspaces) {
    if (workspace.workspace_type?.typeName) {
      sizes.add(workspace.workspace_type.typeName);
    }
  }
  return Array.from(sizes).sort();
}

function matchStatus(workspace: WorkspaceT, statusFilter: StatusFilter): boolean {
  if (statusFilter === 'all') {
    return true;
  }

  switch (statusFilter) {
    case 'active':
      return isStatusActive(workspace.status) && !isStatusDegraded(workspace.status);
    case 'stopped':
      return isStatusPaused(workspace.status);
    case 'idle':
      return isStatusIdle(workspace.status);
    case 'pending':
      return isStatusPending(workspace.status);
    case 'error':
      return isStatusError(workspace.status);
    case 'degraded':
      return isStatusDegraded(workspace.status);
    case 'terminating':
      return workspace.status === 'Terminating';
    default:
      return true;
  }
}

function matchAccess(workspace: WorkspaceT, accessFilter: AccessFilter): boolean {
  if (accessFilter === 'all') {
    return true;
  }
  return accessFilter === 'rw' ? workspace.is_rw : !workspace.is_rw;
}

function matchSearch(row: WorkspaceRow, searchInput: string): boolean {
  const search = searchInput.trim().toLowerCase();
  if (!search) {
    return true;
  }
  return (
    row.workspace.name.toLowerCase().includes(search) ||
    (row.databaseName ? row.databaseName.toLowerCase().includes(search) : false)
  );
}

function compareRows(a: WorkspaceRow, b: WorkspaceRow, sort: SortKey): number {
  switch (sort) {
    case 'name_asc':
      return a.workspace.name.localeCompare(b.workspace.name);
    case 'name_desc':
      return b.workspace.name.localeCompare(a.workspace.name);
    case 'size_asc':
      return (a.workspace.workspace_type?.typeName || '').localeCompare(b.workspace.workspace_type?.typeName || '');
    case 'size_desc':
      return (b.workspace.workspace_type?.typeName || '').localeCompare(a.workspace.workspace_type?.typeName || '');
    default:
      return a.workspace.name.localeCompare(b.workspace.name);
  }
}

export function applyWorkspaceFilters(rows: WorkspaceRow[], filters: FilterState): WorkspaceRow[] {
  const filtered = rows.filter(
    (row) =>
      matchSearch(row, filters.search) &&
      matchStatus(row.workspace, filters.status) &&
      matchAccess(row.workspace, filters.access) &&
      (filters.size === 'all' || row.workspace.workspace_type?.typeName === filters.size)
  );

  return filtered.sort((a, b) => compareRows(a, b, filters.sort));
}

export function formatConfigurationLabel(workspace: WorkspaceT): string {
  const typeName = workspace.workspace_type?.typeName;
  const memory = workspace.workspace_type?.memory;
  if (!typeName) {
    return '-';
  }
  return memory ? `${typeName} (${memory})` : typeName;
}
